import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Users, ClipboardList } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import dashboardApi, { DashboardData, handleApiError } from '../services/dashboardService';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import RemindersWidget from '../components/dashboard/RemindersWidget';
import TopPerformersWidget from '../components/dashboard/TopPerformersWidget';
import AdminFinancialOverview from '../components/dashboard/admin/AdminFinancialOverview';
import AdminSalesMetrics from '../components/dashboard/admin/AdminSalesMetrics';
import AdminCasesMetrics from '../components/dashboard/admin/AdminCasesMetrics';
import AdminSpecialPaymentsWidget from '../components/dashboard/admin/AdminSpecialPaymentsWidget';
import DepartmentBreakdownWidget from '../components/dashboard/admin/DepartmentBreakdownWidget';
import CashFlowTrendChart from '../components/dashboard/admin/CashFlowTrendChart';
import AdminAlertsWidget from '../components/dashboard/admin/AdminAlertsWidget';
import StatusDistributionCard from '../components/dashboard/StatusDistributionCard';
import CalendarWidget from '../components/dashboard/CalendarWidget';
import DashboardNavigation, { getAdminDashboardSections } from '../components/dashboard/DashboardNavigation';
import BackToTopButton from '../components/dashboard/BackToTopButton';

const DashboardContainer = styled.div`
  padding: 32px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 18px;
  color: var(--text-secondary);
`;

const ErrorContainer = styled.div`
  background: var(--error-light);
  color: var(--error-color);
  padding: 16px;
  border-radius: var(--border-radius-md);
  border: 1px solid var(--error-color);
  margin-bottom: 24px;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
`;

const StatusDistributionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.section`
  scroll-margin-top: 100px;
`;

// הודעה מיידעת (לא שגיאה) — מוצגת כשחלק מהנתונים אינו זמין אך העמוד עצמו תקין
const InfoNotice = styled.div`
  background: var(--background-secondary);
  color: var(--text-secondary);
  padding: 16px;
  border-radius: var(--border-radius-md);
  border: 1px solid var(--border-color);
  font-size: 14px;
`;

const DashboardPage: React.FC = () => {
  const { user, isManager, isStaff } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dashboardApi.getDashboardData();
      setDashboardData(data);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const handleReminderUpdate = () => {
    // Reload dashboard data when reminders are updated
    loadDashboardData();
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <DashboardContainer>
        <LoadingContainer>
          טוען נתוני דשבורד...
        </LoadingContainer>
      </DashboardContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <ErrorContainer>
          {error}
        </ErrorContainer>
      </DashboardContainer>
    );
  }

  if (!dashboardData) {
    return null;
  }

  return (
    <DashboardContainer>
      <DashboardHeader />

      {/* Sticky Navigation for System Admin */}
      {isManager && (
        dashboardData?.systemAdminData && (
          <DashboardNavigation sections={getAdminDashboardSections()} />
        )
      )}

      <DashboardGrid>
        {/* יומן — במרכז התצוגה לכל הצוות (Manager + Employee) */}
        {isStaff && (
          <Section id="calendar">
            <CalendarWidget />
          </Section>
        )}

        {/* תזכורות אישיות — כל הצוות (Manager + Employee) */}
        {isStaff && (
          <Section id="reminders">
            <RemindersWidget
              reminders={{
                today: dashboardData.remindersSummary.todayReminders,
                overdue: dashboardData.remindersSummary.overdueReminders,
                upcoming: dashboardData.remindersSummary.upcomingReminders
              }}
              counts={{
                todayCount: dashboardData.remindersSummary.todayCount,
                overdueCount: dashboardData.remindersSummary.overdueCount,
                upcomingCount: dashboardData.remindersSummary.upcomingCount
              }}
              onReminderUpdate={handleReminderUpdate}
            />
          </Section>
        )}

        {/* מובילי החודש — כל הצוות (Manager + Employee) */}
        {isStaff && dashboardData.topPerformers && (
          <Section id="top-performers">
            <TopPerformersWidget performers={dashboardData.topPerformers} />
          </Section>
        )}

        {/* ווידג׳טים ארגוניים — מנהל בלבד */}
        {isManager && !dashboardData.systemAdminData && (
          <InfoNotice>
            הנתונים הארגוניים אינם זמינים כרגע. שאר חלקי הדשבורד פעילים — נסה לרענן את העמוד.
          </InfoNotice>
        )}

        {isManager && (
          dashboardData.systemAdminData && (
            <>
              {/* Alerts & Urgent Tasks - MOVED TO TOP */}
              <Section id="alerts">
                <AdminAlertsWidget data={dashboardData.systemAdminData.urgentTasksSummary} />
              </Section>

              {/* Status Distribution Cards - NEW */}
              <Section id="status-distribution">
                <StatusDistributionGrid>
                  {dashboardData.systemAdminData.clientStatusDistribution && (
                    <StatusDistributionCard
                      title="התפלגות סטטוסי לקוחות"
                      statusDistribution={dashboardData.systemAdminData.clientStatusDistribution}
                      icon={<Users size={20} />}
                    />
                  )}
                  {dashboardData.systemAdminData.leadStatusDistribution && (
                    <StatusDistributionCard
                      title="התפלגות סטטוסי לידים"
                      statusDistribution={dashboardData.systemAdminData.leadStatusDistribution}
                      icon={<ClipboardList size={20} />}
                    />
                  )}
                </StatusDistributionGrid>

                {/* מדדי מכירות ותיקים — חלק מסעיף "ביצועים" ולכן בתוך אותו Section */}
                <AdminSalesMetrics
                  performance={dashboardData.systemAdminData.systemPerformance}
                  pipeline={dashboardData.systemAdminData.systemPipeline}
                  newLeadsCount={dashboardData.systemAdminData.systemNewLeadsCount}
                  signedThisMonthCount={dashboardData.systemAdminData.systemSignedThisMonthCount}
                />

                <AdminCasesMetrics
                  metrics={dashboardData.systemAdminData.casesMetrics}
                />
              </Section>

              {/* Financial Overview + Cash Flow */}
              <Section id="financial">
                <AdminFinancialOverview data={dashboardData.systemAdminData.financialOverview} />
                <CashFlowTrendChart data={dashboardData.systemAdminData.cashFlowTrend} />
              </Section>

              {/* Special Payments Widget */}
              <Section id="special-payments">
                {dashboardData.systemAdminData.specialPaymentsSummary && (
                  <AdminSpecialPaymentsWidget
                    data={dashboardData.systemAdminData.specialPaymentsSummary}
                  />
                )}
              </Section>

            </>
          )
        )}

        {/* פילוח לפי מחלקות — מנהל בלבד (תצוגת סיווג).
            שדה DTO עצמאי, ולכן אינו תלוי ב-systemAdminData */}
        {isManager && dashboardData.departmentBreakdown && (
          <Section id="departments">
            <DepartmentBreakdownWidget departments={dashboardData.departmentBreakdown} />
          </Section>
        )}
      </DashboardGrid>

      {/* Back to Top Button */}
      <BackToTopButton />
    </DashboardContainer>
  );
};

export default DashboardPage;